import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { formatNumber, formatPeriod } from '../utils/dataUtils';

interface UtilizationUserRankingTableProps {
  data: UserRankingDto[];
  onItemClick?: (user: UserRankingDto) => void;
  showMetric?: 'sessions' | 'minutes';
  maxRows?: number;
  compactView?: boolean;
  isLumenaAdmin?: boolean;
}

export function UtilizationUserRankingTable({
  data,
  onItemClick,
  showMetric = 'sessions',
  maxRows = 50,
  compactView = false,
  isLumenaAdmin = true,
}: UtilizationUserRankingTableProps) {
  const displayData = data.slice(0, maxRows);

  if (!displayData.length) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--anl-text-muted)]">
        <p>No member data available</p>
      </div>
    );
  }

  const handleRowClick = (user: UserRankingDto) => {
    if (onItemClick) {
      onItemClick(user);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-[var(--anl-border)]">
        <thead className="bg-[var(--anl-surface-elev)]">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]">
              Rank
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]">
              Member
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]">
              {showMetric === 'sessions' ? 'Sessions' : 'Minutes'}
            </th>
            {!compactView && (
              <>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]">
                  {showMetric === 'sessions' ? 'Total Minutes' : 'Total Sessions'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-[var(--anl-text-muted)]">
                  This month sessions
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-[var(--anl-text-muted)]">
                  This month minutes
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]">
                  Most Used Experience
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]">
                  Last Session
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--anl-border)] bg-[var(--anl-surface)]">
          {displayData.map((user, index) => (
            <tr
              key={`${user.user_id || 'unknown'}-${user.first_name}-${user.last_name}-${index}`}
              className={`${isLumenaAdmin && onItemClick ? 'cursor-pointer hover:bg-[var(--anl-surface-elev)]' : ''}`}
              onClick={() => isLumenaAdmin && handleRowClick(user)}
            >
              <td className="whitespace-nowrap px-4 py-3">
                <div className="flex items-center">
                  <div
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                    style={{
                      backgroundColor: 'var(--anl-metric-bg)',
                      color: 'var(--anl-text)',
                    }}
                  >
                    <span className="text-xs font-medium">{index + 1}</span>
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                <div className="text-sm font-medium text-[var(--anl-text)]">
                  {`${user.first_name} ${user.last_name}`}
                </div>
                <div className="text-sm text-[var(--anl-text-muted)]">{user.user_type}</div>
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-right">
                <span className="text-sm font-medium text-[var(--anl-text)]">
                  {formatNumber(
                    showMetric === 'sessions'
                      ? user.total_sessions
                      : Math.round(user.total_minutes),
                  )}
                </span>
              </td>
              {!compactView && (
                <>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <span className="text-sm text-[var(--anl-text-muted)]">
                      {formatNumber(
                        showMetric === 'sessions'
                          ? Math.round(user.total_minutes)
                          : user.total_sessions,
                      )}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-[var(--anl-text-muted)]">
                    {formatNumber(user.sessions_this_month)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-[var(--anl-text-muted)]">
                    {formatNumber(Math.round(user.minutes_this_month))}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-[var(--anl-text-muted)]">
                    {user.most_used_experience || '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-[var(--anl-text-muted)]">
                    {user.last_session_date ? (
                      <div>
                        <div>{formatPeriod(user.last_session_date, 'date')}</div>
                        <div className="text-xs">
                          {user.days_since_last_session !== null &&
                          user.days_since_last_session !== undefined
                            ? `${user.days_since_last_session} days ago`
                            : ''}
                        </div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
